/**
 * My Training Records Page - User's personal training history as a sortable table
 */

import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { learningApi } from '../api/learningApi'
import type { TrainingType, VerificationStatus } from '../types/learning.types'

const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, string> = {
  pending: 'Pending',
  verified: 'Verified',
  rejected: 'Rejected',
}

const VERIFICATION_STATUS_COLORS: Record<VerificationStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  verified: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
}

const TRAINING_TYPE_LABELS: Record<TrainingType, string> = {
  course: 'Course',
  workshop: 'Workshop',
  certification: 'Certification',
  webinar: 'Webinar',
  seminar: 'Seminar',
  conference: 'Conference',
  other: 'Other',
}

const TRAINING_TYPE_COLORS: Record<TrainingType, string> = {
  course: 'bg-red-100 text-red-700',
  workshop: 'bg-rose-100 text-rose-700',
  certification: 'bg-green-100 text-green-700',
  webinar: 'bg-orange-100 text-orange-700',
  seminar: 'bg-pink-100 text-pink-700',
  conference: 'bg-indigo-100 text-indigo-700',
  other: 'bg-gray-100 text-gray-700',
}

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i)

const LIMIT = 25

type SortField = 'training_name' | 'start_date' | 'end_date' | 'rating' | 'verification_status'
type SortOrder = 'asc' | 'desc'

const SortIcon = ({ active, order }: { active: boolean; order: SortOrder }) => {
  if (!active) {
    return (
      <svg className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    )
  }
  return order === 'asc' ? (
    <svg className="w-3.5 h-3.5 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-3.5 h-3.5 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

const MyTrainingRecordsPage = () => {
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [yearFilter, setYearFilter] = useState<number | undefined>(undefined)
  const [statusFilter, setStatusFilter] = useState<VerificationStatus | ''>('')
  const [showDeleted, setShowDeleted] = useState(false)
  const [sortField, setSortField] = useState<SortField>('start_date')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const queryParams = useMemo(() => ({
    page,
    limit: LIMIT,
    year: yearFilter,
    verification_status: statusFilter || undefined,
    include_deleted_training: showDeleted,
    sort_by: sortField,
    sort_order: sortOrder,
  }), [page, yearFilter, statusFilter, showDeleted, sortField, sortOrder])

  const { data, isLoading } = useQuery({
    queryKey: ['myTrainingRecords', queryParams],
    queryFn: () => learningApi.getMyRecords(queryParams),
  })

  const records = data?.data || []
  const pagination = data?.pagination

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder(field === 'start_date' ? 'desc' : 'asc')
    }
    setPage(1)
  }

  const hasActiveFilters = yearFilter !== undefined || statusFilter !== '' || showDeleted

  const pageNumbers = (() => {
    if (!pagination || pagination.pages <= 1) return []
    const total = pagination.pages
    const range: number[] = []
    const left = Math.max(1, page - 2)
    const right = Math.min(total, page + 2)
    for (let i = left; i <= right; i++) range.push(i)
    if (left > 2) range.unshift(-1, 1)
    else if (left === 2) range.unshift(1)
    if (right < total - 1) range.push(-2, total)
    else if (right === total - 1) range.push(total)
    return range
  })()

  const ThSortable = ({ field, children, className = '' }: { field: SortField; children: React.ReactNode; className?: string }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 select-none transition-colors ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1.5">
        {children}
        <SortIcon active={sortField === field} order={sortOrder} />
      </div>
    </th>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-2">
            <Link to="/learning" className="hover:text-white transition-colors">Learning</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">My Training Records</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">My Training Records</h1>
              <p className="text-red-200">Track your learning journey and certifications</p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/learning/trainings"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-all border border-white/20"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Browse Trainings
              </Link>
              <Link
                to="/learning/records/new"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 font-medium rounded-xl hover:bg-red-50 transition-all shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Training
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-6 pb-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
          <div className="flex flex-wrap gap-3 items-center">
            <select
              value={yearFilter ?? ''}
              onChange={(e) => { setYearFilter(e.target.value ? parseInt(e.target.value) : undefined); setPage(1) }}
              className="px-4 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-red-500 text-gray-900 text-sm"
            >
              <option value="">All Years</option>
              {yearOptions.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as VerificationStatus | ''); setPage(1) }}
              className="px-4 py-2.5 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-red-500 text-gray-900 text-sm"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>

            <label className="flex items-center gap-2.5 cursor-pointer ml-1">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => { setShowDeleted(e.target.checked); setPage(1) }}
                className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
              />
              <span className="text-sm text-gray-600">Show deleted trainings</span>
            </label>

            {hasActiveFilters && (
              <button
                onClick={() => { setYearFilter(undefined); setStatusFilter(''); setShowDeleted(false); setPage(1) }}
                className="px-4 py-2.5 text-sm text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
          <div className="px-6 py-3 border-b border-gray-100">
            <span className="text-sm text-gray-500">
              {isLoading ? (
                <span className="inline-block w-28 h-4 bg-gray-100 rounded animate-pulse" />
              ) : pagination ? (
                <>
                  Showing{' '}
                  <span className="font-semibold text-gray-900">{(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, pagination.total)}</span>
                  {' '}of{' '}
                  <span className="font-semibold text-gray-900">{pagination.total}</span> records
                </>
              ) : `${records.length} records`}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 pl-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-8">#</th>
                  <ThSortable field="training_name" className="min-w-[200px]">Training</ThSortable>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[130px]">Provider</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[110px]">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[90px]">Duration</th>
                  <ThSortable field="start_date" className="min-w-[110px]">Start Date</ThSortable>
                  <ThSortable field="end_date" className="min-w-[100px]">End Date</ThSortable>
                  <ThSortable field="rating" className="min-w-[80px]">Rating</ThSortable>
                  <ThSortable field="verification_status" className="min-w-[110px]">Status</ThSortable>
                  <th className="px-4 py-3 pr-6 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[70px]">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3.5 pl-6"><div className="h-4 bg-gray-100 rounded w-5" /></td>
                      <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-44" /></td>
                      <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-28" /></td>
                      <td className="px-4 py-3.5"><div className="h-5 bg-gray-100 rounded-full w-20" /></td>
                      <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-16" /></td>
                      <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-20" /></td>
                      <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-20" /></td>
                      <td className="px-4 py-3.5"><div className="h-4 bg-gray-100 rounded w-12" /></td>
                      <td className="px-4 py-3.5"><div className="h-5 bg-gray-100 rounded-full w-18" /></td>
                      <td className="px-4 py-3.5 pr-6"><div className="h-4 bg-gray-100 rounded w-8" /></td>
                    </tr>
                  ))
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-16 text-center">
                      <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <p className="text-gray-500 font-medium">No training records found</p>
                      {hasActiveFilters ? (
                        <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
                      ) : (
                        <Link
                          to="/learning/records/new"
                          className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Your First Training
                        </Link>
                      )}
                    </td>
                  </tr>
                ) : (
                  records.map((record, index) => {
                    const isDeletedTraining = !!record.training.deleted_at
                    const isInactiveTraining = !isDeletedTraining && record.training.is_active === false
                    return (
                      <tr
                        key={record.id}
                        className={`hover:bg-red-50/40 cursor-pointer transition-colors ${isDeletedTraining ? 'opacity-60' : ''}`}
                        onClick={() => navigate(`/learning/records/${record.id}`)}
                      >
                        <td className="px-4 py-3.5 pl-6 text-sm text-gray-400">
                          {(page - 1) * LIMIT + index + 1}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 text-sm line-clamp-1">{record.training.name}</span>
                            {isDeletedTraining && (
                              <span className="shrink-0 px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-600">Deleted</span>
                            )}
                            {isInactiveTraining && (
                              <span className="shrink-0 px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-500">Inactive</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {record.training.provider || <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${TRAINING_TYPE_COLORS[record.training.training_type]}`}>
                            {TRAINING_TYPE_LABELS[record.training.training_type]}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {record.training.duration_hours
                            ? `${record.training.duration_hours % 1 === 0 ? record.training.duration_hours : record.training.duration_hours.toFixed(1)} hrs`
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {format(new Date(record.start_date), 'dd MMM yyyy')}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {record.end_date ? format(new Date(record.end_date), 'dd MMM yyyy') : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3.5">
                          {record.rating ? (
                            <div className="flex items-center gap-1">
                              <svg className="w-3.5 h-3.5 text-yellow-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <span className="text-sm font-medium text-gray-700">{record.rating.toFixed(1)}</span>
                            </div>
                          ) : <span className="text-gray-300 text-sm">—</span>}
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${VERIFICATION_STATUS_COLORS[record.verification_status]}`}>
                            {VERIFICATION_STATUS_LABELS[record.verification_status]}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 pr-6">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/learning/records/${record.id}`) }}
                            className="text-red-600 hover:text-red-800 text-sm font-medium transition-colors"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              Page <span className="font-semibold text-gray-900">{page}</span> of{' '}
              <span className="font-semibold text-gray-900">{pagination.pages}</span>
              {' '}· {pagination.total} total records
            </p>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="First page">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <button onClick={() => setPage(p => p - 1)} disabled={page === 1}
                className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Prev
              </button>

              {pageNumbers.map((n, i) =>
                n < 0 ? (
                  <span key={`e-${i}`} className="px-1 text-gray-400">…</span>
                ) : (
                  <button key={n} onClick={() => setPage(n)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${n === page ? 'bg-red-600 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                    {n}
                  </button>
                )
              )}

              <button onClick={() => setPage(p => p + 1)} disabled={page >= pagination.pages}
                className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Next
              </button>
              <button onClick={() => setPage(pagination.pages)} disabled={page >= pagination.pages}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Last page">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MyTrainingRecordsPage
